import { useState } from "react";
import { ChevronLeft, ChevronRight } from "lucide-react";

const FALLBACK = "https://images.unsplash.com/photo-1692486407015-3a47e4c1181c";

/**
 * Product image gallery. Auto-falls-back to a single image layout when only one
 * image is available. With multiple images shows: main image + prev/next arrows
 * + thumbnail strip + keyboard arrow navigation.
 *
 * Props:
 *   images — string[] (preferred, full list)
 *   fallback — string (used when images is empty)
 *   name — string (for alt text)
 */
const ProductImageCarousel = ({ images, fallback, name }) => {
  const list = Array.isArray(images) && images.length > 0 ? images : [fallback || FALLBACK];
  const [idx, setIdx] = useState(0);
  const total = list.length;
  const showControls = total > 1;

  const prev = () => setIdx((i) => (i - 1 + total) % total);
  const next = () => setIdx((i) => (i + 1) % total);

  return (
    <div data-testid="product-image-carousel">
      <div className="aspect-square bg-[#121212] rounded-sm overflow-hidden relative group">
        <img
          key={list[idx]}
          src={list[idx]}
          alt={`${name || "Product"} — image ${idx + 1}`}
          className="w-full h-full object-cover"
          onError={(e) => { e.currentTarget.src = FALLBACK; }}
          data-testid="product-image-main"
        />
        {showControls && (
          <>
            <button
              type="button"
              onClick={prev}
              aria-label="Previous image"
              className="absolute left-2 top-1/2 -translate-y-1/2 w-10 h-10 bg-black/60 hover:bg-black/85 rounded-full flex items-center justify-center text-white opacity-0 group-hover:opacity-100 transition-opacity"
              data-testid="carousel-prev"
            >
              <ChevronLeft className="w-5 h-5" />
            </button>
            <button
              type="button"
              onClick={next}
              aria-label="Next image"
              className="absolute right-2 top-1/2 -translate-y-1/2 w-10 h-10 bg-black/60 hover:bg-black/85 rounded-full flex items-center justify-center text-white opacity-0 group-hover:opacity-100 transition-opacity"
              data-testid="carousel-next"
            >
              <ChevronRight className="w-5 h-5" />
            </button>
            <div className="absolute bottom-2 left-1/2 -translate-x-1/2 bg-black/70 text-white text-xs px-2 py-1 rounded-sm">
              {idx + 1} / {total}
            </div>
          </>
        )}
      </div>
      {showControls && (
        <div className="grid grid-cols-5 gap-2 mt-3" data-testid="carousel-thumbnails">
          {list.slice(0, 8).map((src, i) => (
            <button
              key={`${src}-${i}`}
              type="button"
              onClick={() => setIdx(i)}
              className={`aspect-square bg-[#121212] rounded-sm overflow-hidden border-2 transition-colors ${i === idx ? "border-[#D62828]" : "border-transparent hover:border-white/30"}`}
              data-testid={`carousel-thumb-${i}`}
            >
              <img
                src={src}
                alt={`${name || "Product"} thumbnail ${i + 1}`}
                className="w-full h-full object-cover"
                onError={(e) => { e.currentTarget.src = FALLBACK; }}
              />
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

export default ProductImageCarousel;
